import React from 'react';
import { Plus, X } from 'lucide-react';
import type { DrillStep, Option } from './types';
import { DIMENSION_HINT_LABELS, MAX_DYNAMIC_ROWS } from './utils';

type DynamicRowSelectorProps = {
  availableDimensions: Option[];
  selectedDimensionKeys: string[];
  drillPath: DrillStep[];
  onRemoveDrillStep: (index: number) => void;
  onAdd: (dimensionKey: string) => void;
  onRemove?: (dimensionKey: string) => void;
};

export function DynamicRowSelector({
  availableDimensions,
  selectedDimensionKeys,
  drillPath,
  onRemoveDrillStep,
  onAdd,
  onRemove,
}: DynamicRowSelectorProps) {
  const [selected, setSelected] = React.useState('');

  const notUsed = React.useMemo(
    () => availableDimensions.filter(option => !selectedDimensionKeys.includes(option.key)),
    [availableDimensions, selectedDimensionKeys]
  );

  const selectedOptions = React.useMemo(
    () => availableDimensions.filter(option => selectedDimensionKeys.includes(option.key)),
    [availableDimensions, selectedDimensionKeys]
  );

  const suggestions = React.useMemo(
    () =>
      notUsed.filter(option => {
        const label = option.label.toLowerCase();
        return DIMENSION_HINT_LABELS.some(hint => label.includes(hint.toLowerCase()));
      }),
    [notUsed]
  );

  const disabled = notUsed.length === 0 || selectedDimensionKeys.length >= MAX_DYNAMIC_ROWS;
  const hasActiveFilters = drillPath.length > 0;

  return (
    <section className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
      <div className={hasActiveFilters ? 'grid gap-4 lg:grid-cols-2' : 'flex flex-col gap-3'}>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col items-start gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Analizar:
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {selectedOptions.map(option => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => onRemove?.(option.key)}
                  className="group inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white transition hover:bg-primary/90"
                >
                  {option.label}
                  <X className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                </button>
              ))}
              {selectedOptions.length === 0 ? (
                <span className="text-xs text-gray-400">Selecciona dimensiones para analizar</span>
              ) : null}
            </div>
          </div>

          {suggestions.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {suggestions.slice(0, 3).map(option => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => onAdd(option.key)}
                  disabled={disabled}
                  className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  + {option.label}
                </button>
              ))}
            </div>
          ) : null}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <select
              className="min-w-[260px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
              value={selected}
              onChange={event => setSelected(event.target.value)}
              disabled={disabled}
            >
              <option value="">Selecciona una dimension...</option>
              {notUsed.map(option => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={disabled || !selected}
              onClick={() => {
                if (!selected) return;
                onAdd(selected);
                setSelected('');
              }}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              Agregar gráfico
            </button>
            <span className="text-xs text-gray-500">Maximo {MAX_DYNAMIC_ROWS} filas</span>
          </div>
        </div>

        {hasActiveFilters ? (
          <div className="rounded-lg border border-gray-200 bg-white/70 px-3 py-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Filtros activos
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {drillPath.map((step, index) => (
                <button
                  key={`${step.dimensionAccessor}-${step.valueKey}-${index}`}
                  type="button"
                  onClick={() => onRemoveDrillStep(index)}
                  className="group inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition hover:bg-primary/20"
                  title={
                    step.secondaryLabel ? `${step.label} (${step.secondaryLabel})` : step.label
                  }
                >
                  {step.dimensionAccessor}: {step.label}
                  <X className="h-3 w-3 opacity-70 transition group-hover:opacity-100" />
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
